import Badge from "@intility/bifrost-react/Badge";
import Input from "@intility/bifrost-react/Input";
import { Fragment, useEffect, useState } from "react";
import type {
  BlueprintNode,
  BlueprintPlatform,
  PlatformName,
} from "~/api/fga-types";
import { deriveSegmentTemplate } from "~/lib/resourceType";
import "./BlueprintBuilder.css";

interface BlueprintReviewProps {
  blueprint: { platforms: BlueprintPlatform[] };
  platforms: PlatformName[];
  /** When set, only this platform (bare Platform slug) is shown. */
  singlePlatform?: string | null;
  /**
   * When provided, qualified names are editable (committed on blur). Omit for a
   * read-only summary (e.g. the platform detail page).
   */
  onUpdateUsage?: (
    usageId: string,
    qualifiedName: string,
  ) => Promise<void> | void;
  /** Hide the per-platform title (e.g. when the page header already shows it). */
  hidePlatformTitle?: boolean;
  /** Stretch to the container width instead of the default 900px reading width. */
  fullWidth?: boolean;
}

interface FlatRow {
  usageId: string;
  typeName: string;
  isStatic: boolean;
  qualifiedName: string | null;
  depth: number;
  path: string;
}

/** The path segment a node contributes: its regex template, or its slug. */
function segmentOf(node: BlueprintNode): string {
  return deriveSegmentTemplate(node.type.nameRegex ?? "") ?? node.type.slug;
}

// platformSlug is the bare Platform slug. A real object id is company-prefixed, so
// each resource path is shown as
// resource:<company>/{platformSlug}/{ancestor segments}/{node segment}.
function flattenWithPaths(
  platformSlug: string,
  nodes: BlueprintNode[],
  ancestors: BlueprintNode[] = [],
  depth = 0,
): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const node of nodes) {
    const segments = [
      "<company>",
      platformSlug,
      ...ancestors.map(segmentOf),
      segmentOf(node),
    ];
    rows.push({
      usageId: node.usageId,
      typeName: node.type.displayName,
      isStatic: node.type.isStatic,
      qualifiedName: node.qualifiedName ?? null,
      depth,
      path: `resource:${segments.join("/")}`,
    });
    rows.push(
      ...flattenWithPaths(
        platformSlug,
        node.children,
        [...ancestors, node],
        depth + 1,
      ),
    );
  }
  return rows;
}

/** Editable qualified name; commits on blur so we don't write per keystroke. */
function QualifiedNameCell({
  usageId,
  value,
  onUpdateUsage,
}: {
  usageId: string;
  value: string | null;
  onUpdateUsage: (
    usageId: string,
    qualifiedName: string,
  ) => Promise<void> | void;
}) {
  const [val, setVal] = useState(value ?? "");
  useEffect(() => {
    setVal(value ?? "");
  }, [value]);

  return (
    <Input
      label="Qualified name"
      hideLabel
      small
      style={{ width: "100%" }}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        const trimmed = val.trim();
        if (trimmed && trimmed !== (value ?? "")) {
          onUpdateUsage(usageId, trimmed);
        }
      }}
    />
  );
}

export default function BlueprintReview({
  blueprint,
  platforms,
  singlePlatform,
  onUpdateUsage,
  hidePlatformTitle,
  fullWidth,
}: BlueprintReviewProps) {
  const platformDisplayName = (platformSlug: string): string => {
    const localSlug = platformSlug.split("/").pop() ?? platformSlug;
    return (
      platforms.find((p) => p.slug === localSlug)?.displayName ?? localSlug
    );
  };

  const shown = singlePlatform
    ? blueprint.platforms.filter((p) => p.platform === singlePlatform)
    : blueprint.platforms;

  const hasResources = shown.some((p) => p.tree.length > 0);

  return (
    <div
      className="bp-review"
      style={fullWidth ? { maxWidth: "none" } : undefined}
    >
      {!hasResources && (
        <p className="bf-p" style={{ color: "var(--bfc-base-c-2)" }}>
          No resources placed yet. An empty blueprint is valid. Access is
          limited to the platform itself, with no resource granularity.
        </p>
      )}

      {shown.map((p) => {
        const rows = flattenWithPaths(p.platform, p.tree);
        if (rows.length === 0) return null;
        return (
          <div key={p.platform}>
            {!hidePlatformTitle && (
              <h3 className="bp-review-platform-title">
                {platformDisplayName(p.platform)}
              </h3>
            )}
            <div className="bp-review-table">
              <span className="bp-review-th">Resource</span>
              <span className="bp-review-th">Qualified name</span>
              <span className="bp-review-th">Path</span>
              {rows.map((row) => (
                <Fragment key={row.usageId}>
                  <span className="bp-review-cell">
                    {row.depth > 0 && (
                      <span className="bp-review-indents">
                        {Array.from({ length: row.depth }).map((_, i) => (
                          <span
                            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-count depth guides
                            key={i}
                            className="bp-review-indent"
                          />
                        ))}
                      </span>
                    )}
                    {row.typeName}
                    <Badge state={row.isStatic ? "neutral" : "chill"}>
                      {row.isStatic ? "Static" : "Dynamic"}
                    </Badge>
                  </span>
                  {onUpdateUsage ? (
                    <QualifiedNameCell
                      usageId={row.usageId}
                      value={row.qualifiedName}
                      onUpdateUsage={onUpdateUsage}
                    />
                  ) : (
                    <span className="bp-review-cell">
                      {row.qualifiedName ? (
                        <code>{row.qualifiedName}</code>
                      ) : (
                        <span style={{ color: "var(--bfc-base-c-2)" }}>—</span>
                      )}
                    </span>
                  )}
                  <code className="bp-review-path">{row.path}</code>
                </Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
